import { Controller } from "@hotwired/stimulus";
import { interpolate } from "../../utils/string_utils";
import { dispatchFilterPreselectedEvent, getFilterLabel } from "../../datatables/datatable_filters";
import { dataTableFunctions } from "../../datatables/datatable_functions";
import { isSelect2Input, selectOptionFor } from "../../utils/inputs_utils";
import { toggleClass } from "../../utils/misc";
import { createBottomPopperElement, deletePopperElement } from "../../utils/popper_utils";

// This controller handles the selected filters.
// It allows to open a selected filter panel so that user can define values for that filter.
// It also handles preselected filters that will be displayed on page load.
export default class extends Controller {
  static targets = [
    "container",
    "filter",
    "selectedFilter",
    "filterTemplate",
    "updatePanel",
    "updatePanelContent",
    "counterContainer",
    "counter",
  ];
  static values = {
    preselectedFilters: Object,
  };

  connect() {
    document.addEventListener("datatable:filterPreselected", this.onFilterPreselected);

    this._buildPreselectedFilters(this.preselectedFiltersValue);

    document.addEventListener("datatable:filterSelected", this.onFilterSelected);

    document.addEventListener("datatable:filterUpdated", this.onFilterUpdated);
  }

  disconnect() {
    document.removeEventListener("datatable:filterPreselected", this.onFilterPreselected);
    document.removeEventListener("datatable:filterSelected", this.onFilterSelected);
    document.removeEventListener("datatable:filterUpdated", this.onFilterUpdated);

    if (this.panelPopperElement) {
      deletePopperElement(this.panelPopperElement);
      this.panelPopperElement = null;
    }
  }

  onFilterPreselected = (event) => {
    this.filterSelected(event);
    this._setFilterPanelValues(event.detail.data);
  };

  onFilterSelected = (event) => {
    this.filterSelected(event);
    this._openPanel(event.detail.name);
  };

  onFilterUpdated = (event) => {
    this.filterUpdated(event);
  };

  _buildPreselectedFilters(filters) {
    // TODO(Jonathan Jalal): Building these "preselected filters" sets the filter panel inputs before the datatable is initialized,
    // but this "assumes" the datatable has not been initialized yet (as there is a HTTP request to fetch the data before initializing it).
    // Ideally, we should find a way to delay the initialization of the datatable until these selected filters (if any) are built.
    for (const [filterType, filterData] of Object.entries(filters)) {
      dispatchFilterPreselectedEvent({
        name: filterType,
        data: filterData.data,
      });
    }
  }

  filterSelected(event) {
    const { name } = event.detail;
    const templateContent = interpolate(this.selectedFilterTarget, {
      filter_name: name,
      filter_label: getFilterLabel(name),
    });

    this.containerTarget.insertAdjacentHTML("beforeend", templateContent);

    this._initializeFilterPanel(name);
    this._handleCounter();
  }

  defaultFilter(e) {
    this.filterTargets.forEach((filter) => {
      this._clearFilter(filter.dataset.filterName);
    });
    const defaultFilters = JSON.parse(e.currentTarget.dataset.preselectedFilters);
    this._buildPreselectedFilters(defaultFilters);
    dataTableFunctions().reloadTable(this._datatableId());
  }

  _initializeFilterPanel(name) {
    const selectedFilter = this._selectedFilter(name);
    const updatePanelContent = this.updatePanelContentTargets.find((target) => selectedFilter.contains(target));
    const selectedFilterTemplate = this.filterTemplateTargets.find((panel) => panel.dataset.filterName === name);
    updatePanelContent.innerHTML = interpolate(selectedFilterTemplate);
  }

  _setFilterPanelValues(data) {
    data.forEach(({ input_id, value }) => {
      const input = document.getElementById(input_id);

      if (["checkbox", "radio"].includes(input.type)) {
        input.checked = true;
      } else if (isSelect2Input(input)) {
        selectOptionFor(input, value);
      } else {
        input.value = value;
      }
    });
  }

  filterUpdated(event) {
    const { keepFilterPanelOpen } = event.detail;

    this._handleCounter();

    if (!keepFilterPanelOpen) {
      this._closeUpdatePanel();
    }
  }

  removeAllFilters() {
    this.filterTargets.forEach((filter) => {
      this._clearFilter(filter.dataset.filterName);
    });
    dataTableFunctions().reloadTable(this._datatableId());
  }

  filterCleared(event) {
    const { filter } = event.params;
    this._clearFilter(filter);
    dataTableFunctions().reloadTable(this._datatableId());
  }

  _clearFilter(filterName) {
    const activeFilter = this._selectedFilter(filterName);
    this._removeSelectedFilter(activeFilter, filterName);
    this._handleCounter();
  }

  openPanel(event) {
    const { filterName } = event.currentTarget.dataset;
    this._openPanel(filterName);
  }

  _openPanel(filterName) {
    this._closeUpdatePanel();

    const selectedFilter = this._selectedFilter(filterName);
    selectedFilter.classList.add("active");

    const updatePanel = this.updatePanelTargets.find((target) => selectedFilter.contains(target));
    updatePanel.classList.add("visible");

    if (this.panelPopperElement) {
      deletePopperElement(this.panelPopperElement);
    }

    this.panelPopperElement = createBottomPopperElement(selectedFilter, updatePanel);
    this.panelPopperElement.scheduleUpdate();
  }

  _closeUpdatePanel() {
    const activeFilter = this.filterTargets.find((target) => target.classList.contains("active"));
    if (activeFilter) {
      activeFilter.classList.remove("active");
      const updatePanel = this.updatePanelTargets.find((target) => activeFilter.contains(target));
      updatePanel.classList.remove("visible");
    }
  }

  _selectedFilter(name) {
    return this.filterTargets.find((filter) => filter.dataset.filterName === name);
  }

  _datatableId() {
    return this.element.dataset.datatableId;
  }

  _removeSelectedFilter(selectedFilterHtml, filterName) {
    selectedFilterHtml.remove();
    document.dispatchEvent(
      new CustomEvent("datatable:filterCleared", { detail: { id: this._datatableId(), name: filterName } })
    );
  }

  _handleCounter() {
    if (this.hasCounterContainerTarget) {
      toggleClass(this.counterContainerTarget, "hidden", this.filterTargets.length > 0);
      this.counterTarget.innerText = this.filterTargets.length;
    }
  }
}
